import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { readForSelect } from '../../person.service';
import Links from '@/shared/constants/navegacao';
import { removerAcentos } from '@/shared/utils/textos';

type PersonOption = { value: string; label: string };

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const TAKE = 20;

interface PersonQuickSearchProps {
  /** Pessoa atualmente aberta — não aparece nos resultados. */
  excludeId?: string | null;
}

export default function PersonQuickSearch({ excludeId }: PersonQuickSearchProps) {
  const navigate = useNavigate();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PersonOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const buscar = useCallback(
    (raw: string) => {
      const filtro = raw.trim();
      if (filtro.length < MIN_CHARS) {
        setOptions([]);
        setLoading(false);
        return;
      }

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      setLoading(true);

      debounceTimer.current = setTimeout(async () => {
        try {
          // API já usa unaccent; normalizamos o termo para o match ser estável no cliente também.
          const [lista] = await readForSelect({
            filtro: removerAcentos(filtro),
            skip: 0,
            take: TAKE,
          });
          const next = lista
            .filter((p) => String(p.id) !== String(excludeId ?? ''))
            .map((p) => ({ value: String(p.id), label: p.nome }));
          setOptions(next);
          setHighlight(0);
          setOpen(true);
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [excludeId],
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const selecionar = (opt: PersonOption) => {
    setQuery('');
    setOptions([]);
    setOpen(false);
    navigate(`${Links.Pessoa_Editar}/${opt.value}`);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || options.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlight];
      if (opt) selecionar(opt);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const mensagemVazia =
    query.trim().length < MIN_CHARS
      ? `Digite ao menos ${MIN_CHARS} caracteres`
      : loading
        ? 'Buscando...'
        : 'Nenhuma pessoa encontrada';

  return (
    <div
      ref={rootRef}
      className="position-relative"
      style={{ minWidth: 240, width: 280 }}
      title="Buscar outra pessoa para editar"
    >
      <input
        id="pessoa-busca-rapida"
        type="text"
        className="form-control form-control-sm"
        placeholder="Buscar pessoa..."
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={query}
        onChange={(e) => {
          const value = e.target.value;
          setQuery(value);
          buscar(value);
        }}
        onFocus={() => {
          if (options.length > 0 || query.trim().length >= MIN_CHARS) setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="list-group position-absolute shadow-sm w-100 mt-1 mb-0"
          style={{ zIndex: 1050, maxHeight: 260, overflowY: 'auto' }}
        >
          {options.length === 0 ? (
            <li className="list-group-item list-group-item-light small text-muted py-2">
              {mensagemVazia}
            </li>
          ) : (
            options.map((opt, index) => (
              <li key={opt.value} className="p-0 border-0">
                <button
                  type="button"
                  role="option"
                  aria-selected={index === highlight}
                  className={`list-group-item list-group-item-action border-0 rounded-0 py-2 px-3 small text-start ${
                    index === highlight ? 'active' : ''
                  }`}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => selecionar(opt)}
                >
                  {opt.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
